import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CLASSIFICATION_OPTIONS = [
  { value: "LTI", label: "LTI — Accident avec arrêt de travail" },
  { value: "MWD", label: "MWD — Accident avec poste aménagé" },
  { value: "MI", label: "MI — Accident sans arrêt" },
  { value: "FAI", label: "FAI — Premiers soins" },
];

export default function NewAnalysis() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isNational = user?.role === "Administrateur national";

  const [sites, setSites] = useState([]);
  const [categories, setCategories] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [classificationBase, setClassificationBase] = useState("");
  const [classificationCritique, setClassificationCritique] = useState(false);
  const [form, setForm] = useState({
    site: "", date_evenement: "", heure_evenement: "", type_evenement: "",
    personne_concernee: "", categorie_personne: "", consequence: "",
    description_initiale: "", localisation: "", activite: "", entreprise: "", equipe_analyse: "",
  });

  useEffect(() => {
    axios.get(`${API}/sites`).then((res) => setSites(res.data));
    axios.get(`${API}/parametres`).then((res) => {
      setCategories(res.data.filter((p) => p.CATEGORIE === "CATEGORIE_PERSONNE"));
    });
  }, []);

  useEffect(() => {
    if (!isNational && user?.site) {
      setForm((f) => ({ ...f, site: user.site }));
    }
  }, [isNational, user]);

  const siteName = useMemo(() => sites.find((s) => s.ID_SITE === form.site)?.NOM_SITE, [sites, form.site]);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const classification = classificationBase ? `${classificationBase}${classificationCritique ? " Critique" : ""}` : "";
      const res = await axios.post(`${API}/hfacs`, { ...form, classification });
      toast.success(`Analyse ${res.data.ID_HFACS} créée.`);
      navigate(`/hfacs/${res.data.ID_HFACS}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de la création de l'analyse.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <AppHeader />
      <main className="max-w-3xl mx-auto px-6 lg:px-10 py-12">
        <h1 className="font-heading font-black uppercase tracking-tight text-3xl mb-2">Nouvelle analyse HFACS</h1>
        <p className="text-sm text-muted-foreground mb-8">
          L'ID HFACS et l'arborescence Google Drive seront générés automatiquement à la création.
        </p>

        <form onSubmit={submit} className="space-y-5 bg-card border border-border rounded-md p-6">
          <div>
            <Label>Site</Label>
            {isNational ? (
              <Select value={form.site} onValueChange={(v) => setForm({ ...form, site: v })}>
                <SelectTrigger data-testid="new-analysis-site-select"><SelectValue placeholder="Sélectionner un site" /></SelectTrigger>
                <SelectContent>
                  {sites.map((s) => <SelectItem key={s.ID_SITE} value={s.ID_SITE}>{s.NOM_SITE} ({s.CODE_SITE})</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <p data-testid="new-analysis-site-locked" className="font-mono text-sm text-muted-foreground py-2">{siteName || user?.site}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="date_evenement">Date événement</Label>
              <Input id="date_evenement" type="date" required data-testid="new-analysis-date" value={form.date_evenement} onChange={update("date_evenement")} />
            </div>
            <div>
              <Label htmlFor="heure_evenement">Heure événement</Label>
              <Input id="heure_evenement" type="time" data-testid="new-analysis-heure" value={form.heure_evenement} onChange={update("heure_evenement")} />
            </div>
          </div>

          <div>
            <Label htmlFor="type_evenement">Type d'événement</Label>
            <Input id="type_evenement" required placeholder="Ex: Chute de hauteur, Coincement..." data-testid="new-analysis-type" value={form.type_evenement} onChange={update("type_evenement")} />
          </div>

          <div className="grid grid-cols-2 gap-4 items-end">
            <div>
              <Label>Classification</Label>
              <Select value={classificationBase} onValueChange={setClassificationBase}>
                <SelectTrigger data-testid="new-analysis-classification-select"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>
                  {CLASSIFICATION_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 pb-2.5">
              <Checkbox id="classification_critique" data-testid="new-analysis-classification-critique" checked={classificationCritique} onCheckedChange={(v) => setClassificationCritique(!!v)} />
              <Label htmlFor="classification_critique" className="cursor-pointer">Incident critique</Label>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="personne_concernee">Personne concernée</Label>
              <Input id="personne_concernee" data-testid="new-analysis-personne" value={form.personne_concernee} onChange={update("personne_concernee")} />
            </div>
            <div>
              <Label>Catégorie</Label>
              <Select value={form.categorie_personne} onValueChange={(v) => setForm({ ...form, categorie_personne: v })}>
                <SelectTrigger data-testid="new-analysis-categorie-select"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c.VALEUR} value={c.VALEUR}>{c.VALEUR}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="description_initiale">Description initiale</Label>
            <Textarea id="description_initiale" required rows={4} data-testid="new-analysis-description" value={form.description_initiale} onChange={update("description_initiale")} />
          </div>

          <div>
            <Label htmlFor="consequence">Conséquence</Label>
            <Textarea id="consequence" rows={2} data-testid="new-analysis-consequence" value={form.consequence} onChange={update("consequence")} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="localisation">Localisation</Label>
              <Input id="localisation" data-testid="new-analysis-localisation" value={form.localisation} onChange={update("localisation")} />
            </div>
            <div>
              <Label htmlFor="activite">Activité</Label>
              <Input id="activite" data-testid="new-analysis-activite" value={form.activite} onChange={update("activite")} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="entreprise">Entreprise</Label>
              <Input id="entreprise" data-testid="new-analysis-entreprise" value={form.entreprise} onChange={update("entreprise")} />
            </div>
            <div>
              <Label htmlFor="equipe_analyse">Équipe d'analyse</Label>
              <Input id="equipe_analyse" data-testid="new-analysis-equipe" value={form.equipe_analyse} onChange={update("equipe_analyse")} />
            </div>
          </div>

          <Button type="submit" disabled={submitting || !form.site} data-testid="new-analysis-submit" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Créer l'analyse"}
          </Button>
        </form>
      </main>
    </div>
  );
}
